import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  country: "",
  product: "Fresh Red Onions",
  quantity: "",
  message: "",
};

export default function QuoteDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.country || !form.quantity) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request submitted! We'll get back to you shortly.");
      setForm(initialForm);
      onOpenChange(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1e5ba8]">Request A Quote</DialogTitle>
          <DialogDescription>
            Tell us your requirement and we&apos;ll send you a competitive quote within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="q-name">Full Name *</Label>
              <Input id="q-name" data-testid="quote-name-input" value={form.name} onChange={update("name")} placeholder="John Doe" />
            </div>
            <div>
              <Label htmlFor="q-email">Email *</Label>
              <Input id="q-email" type="email" data-testid="quote-email-input" value={form.email} onChange={update("email")} placeholder="john@company.com" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="q-phone">Phone *</Label>
              <Input id="q-phone" data-testid="quote-phone-input" value={form.phone} onChange={update("phone")} placeholder="+1 555 0100" />
            </div>
            <div>
              <Label htmlFor="q-country">Country *</Label>
              <Input id="q-country" data-testid="quote-country-input" value={form.country} onChange={update("country")} placeholder="United States" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Product *</Label>
              <Select value={form.product} onValueChange={update("product")}>
                <SelectTrigger data-testid="quote-product-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Fresh Red Onions">Fresh Red Onions</SelectItem>
                  <SelectItem value="Nashik Onions">Nashik Onions</SelectItem>
                  <SelectItem value="Pune Onions">Pune Onions</SelectItem>
                  <SelectItem value="Packed Onions">Packed Onions</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="q-qty">Quantity Required *</Label>
              <Input id="q-qty" data-testid="quote-quantity-input" value={form.quantity} onChange={update("quantity")} placeholder="e.g. 20 MT" />
            </div>
          </div>

          <div>
            <Label htmlFor="q-msg">Message</Label>
            <Textarea id="q-msg" data-testid="quote-message-input" rows={3} value={form.message} onChange={update("message")} placeholder="Any specific requirements..." />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="quote-submit-btn"
            className="w-full bg-[#1e5ba8] hover:bg-[#164582] text-white font-semibold py-2.5"
          >
            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Request"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
